import { useState, useEffect } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Input from "../../../UI/Input/Input";
import Button from "../../../UI/Button/Button";
import server, { getMinistries } from "../../../../apis/server";
import ReactQuill from "react-quill-new";

import classes from "./Create.module.scss";

const CreateEvent = ({
  eventFromList,
  clearSelectedEvent,
  events,
  setEvents,
  setSelectedRows,
}) => {
  const { authUser } = useAuth();
  const navigate = useNavigate();

  const [publish, setPublish] = useState(
    eventFromList && eventFromList.published ? true : false,
  );
  const [publishEnabled, setPublishEnabled] = useState(
    eventFromList ? true : false,
  );
  const [saveEnabled, setSaveEnabled] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(
    eventFromList ? eventFromList : null,
  );

  const [descriptionValue, setDescriptionValue] = useState(
    eventFromList ? eventFromList.description : "",
  );

  const [eventForm, setEventForm] = useState({
    name: {
      elementType: "input",
      elementConfig: {
        type: "text",
        placeholder: "Event name",
      },
      value: selectedEvent ? selectedEvent.name : "",
      validation: {
        required: true,
      },
      width: "70%",
    },
    startDate: {
      elementType: "date",
      elementConfig: {
        timeInputLabel: "Time:",
        dateFormat: "MM/dd/yyyy h:mm aa",
        showTimeInput: true,
        placeholder: "Start date & time",
      },
      value: selectedEvent ? new Date(selectedEvent.startDate) : new Date(),
      validation: {
        required: true,
      },
    },
    endDate: {
      elementType: "date",
      elementConfig: {
        timeInputLabel: "Time:",
        dateFormat: "MM/dd/yyyy h:mm aa",
        showTimeInput: true,
        placeholder: "End date & time",
      },
      value: selectedEvent ? new Date(selectedEvent.endDate) : new Date(),
      validation: {
        required: true,
      },
    },
    repeatsEveryXDays: {
      elementType: "input",
      elementConfig: {
        type: "number",
        placeholder: "Repeats every X days",
        min: 0,
      },
      value:
        selectedEvent && selectedEvent.repeatsEveryXDays
          ? selectedEvent.repeatsEveryXDays
          : "",
      validation: {
        required: false,
      },
      width: "19rem",
    },
    location: {
      elementType: "input",
      elementConfig: {
        type: "text",
        placeholder: "Location",
      },
      value: selectedEvent ? selectedEvent.location : "",
      validation: {
        required: true,
      },
      width: "35rem",
    },
    description: {
      elementType: "richtext",
    },
    image: {
      elementType: "image",
      elementConfig: {
        placeholder: "Image",
        url: selectedEvent?.Image?.url || "",
        file: "",
      },
      value: "",
      validation: {
        required: false,
      },
    },
    embedCode: {
      elementType: "textarea",
      elementConfig: {
        minRows: 8,
        placeholder: "Embed Code (optional)",
      },
      value: selectedEvent ? selectedEvent.embedCode : "",
      validation: {
        required: false,
      },
    },
    ministries: {
      elementType: "select",
      elementConfig: {
        options: [],
        multiple: true,
        placeholder: "Ministries",
      },
      value: selectedEvent?.Ministries
        ? selectedEvent.Ministries.map((m) => m.id.toString())
        : [],
      validation: {
        required: false,
      },
    },
  });

  // EFFECT 1: Runs on mount to get ministries
  useEffect(() => {
    getMinistries(authUser, (updatedFormOrUpdater) => {
      setEventForm((prevForm) => {
        const incoming =
          typeof updatedFormOrUpdater === "function"
            ? updatedFormOrUpdater(prevForm)
            : updatedFormOrUpdater;

        return {
          ...prevForm,
          ...incoming,
          description: prevForm.description || { elementType: "richtext" },
        };
      });
    });
  }, [authUser]);

  // EFFECT 2: Syncs state whenever selectedEvent changes (on load, save, or row click)
  useEffect(() => {
    if (selectedEvent) {
      setPublish(!!selectedEvent.published);

      if (selectedEvent.id) {
        setPublishEnabled(true);
      }

      setDescriptionValue(selectedEvent.description || "");

      // Safely update form values to match the newly selected event
      setEventForm((prevForm) => ({
        ...prevForm,
        description: { elementType: "richtext" },
        name: { ...prevForm.name, value: selectedEvent.name || "" },
        startDate: {
          ...prevForm.startDate,
          value: selectedEvent.startDate
            ? new Date(selectedEvent.startDate)
            : new Date(),
        },
        endDate: {
          ...prevForm.endDate,
          value: selectedEvent.endDate
            ? new Date(selectedEvent.endDate)
            : new Date(),
        },
        repeatsEveryXDays: {
          ...prevForm.repeatsEveryXDays,
          value: selectedEvent.repeatsEveryXDays || "",
        },
        location: { ...prevForm.location, value: selectedEvent.location || "" },
        embedCode: {
          ...prevForm.embedCode,
          value: selectedEvent.embedCode || "",
        },
        image: {
          ...prevForm.image,
          elementConfig: {
            ...prevForm.image.elementConfig,
            url: selectedEvent?.Image?.url || "",
          },
        },
        ministries: {
          ...prevForm.ministries,
          value: selectedEvent.Ministries
            ? selectedEvent.Ministries.map((m) => m.id.toString())
            : [],
        },
      }));
    }
  }, [selectedEvent]);

  // Define allowed formats (excluding 'color' and 'background')
  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "link",
    "image",
    "list",
  ];

  // Define modules with clipboard matcher to strip inline styles on paste
  // Define modules (Quill's format whitelist handles stripping inline color/background formats on paste)
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const handlePublish = async (e) => {
    e.preventDefault();

    try {
      const response = await server.put("/event/publish", {
        eventId: selectedEvent.id,
        published: !publish,
        orgId: authUser.orgId,
      });

      const updatedEvent = response.data;

      setSelectedEvent(updatedEvent);
      setPublish(updatedEvent.published);

      if (events) {
        const revisedEvents = events.map((event) => {
          return event.id === updatedEvent.id ? updatedEvent : event;
        });
        setEvents(revisedEvents);
      }
    } catch (err) {
      console.error("Failed to toggle publish status:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let eventFormValues = new FormData();
    eventFormValues.append("name", eventForm.name.value);
    eventFormValues.append("startDate", eventForm.startDate.value);
    eventFormValues.append("endDate", eventForm.endDate.value);

    const repeats = eventForm.repeatsEveryXDays.value;
    eventFormValues.append(
      "repeatsEveryXDays",
      repeats === "" || repeats === undefined ? 0 : repeats,
    );

    eventFormValues.append("location", eventForm.location.value);
    eventFormValues.append("description", descriptionValue);
    eventFormValues.append("image", eventForm.image.elementConfig.file);
    eventFormValues.append("userId", authUser.id);
    eventFormValues.append("orgId", authUser.orgId);

    eventFormValues.append("published", publish);
    eventFormValues.append(
      "ministryId",
      Array.isArray(eventForm.ministries.value)
        ? eventForm.ministries.value.join(",")
        : eventForm.ministries.value || "",
    );
    eventFormValues.append("embedCode", eventForm.embedCode.value || "");

    let eventResponse;

    const hasValidId =
      selectedEvent && selectedEvent.id && selectedEvent.id !== "undefined";

    try {
      if (hasValidId) {
        eventFormValues.append("id", selectedEvent.id);
        eventResponse = await server.put("/event", eventFormValues);
      } else {
        eventResponse = await server.post("/event", eventFormValues, {
          headers: { "content-type": "multipart/form-data" },
        });
      }

      const res = eventResponse;

      if (res.status === 200 && Array.isArray(res.data)) {
        const justUpdated = res.data.find((ev) => {
          if (hasValidId) {
            return ev.id === selectedEvent.id;
          }
          return ev.name === eventForm.name.value;
        });

        if (justUpdated) {
          setSelectedEvent(justUpdated);
          setPublish(!!justUpdated.published);
          setEvents(res.data);
          setPublishEnabled(true);
          setSaveEnabled(false);
        }
      } else {
        throw new Error("Server response was not an array.");
      }
    } catch (err) {
      console.error("Save Error:", err);
      alert("Save failed: " + (err.response?.data?.error || err.message));
    }
  };

  const inputChangedHandler = (e, inputIdentifier) => {
    setSaveEnabled(true);
    const updatedEventForm = { ...eventForm };
    const updatedFormElement = { ...updatedEventForm[inputIdentifier] };

    if (updatedFormElement.elementType === "date") {
      updatedFormElement.value = e;
    } else if (updatedFormElement.elementType === "image") {
      updatedFormElement.elementConfig.url = URL.createObjectURL(
        e.target.files[0],
      );
      updatedFormElement.elementConfig.file = e.target.files[0];
    } else if (updatedFormElement.elementType === "select") {
      const selectedOptions = Array.from(e.target.selectedOptions).map(
        (option) => option.value,
      );
      updatedFormElement.value = selectedOptions;
    } else {
      updatedFormElement.value = e.target.value;
    }

    updatedFormElement.touched = true;
    updatedEventForm[inputIdentifier] = updatedFormElement;
    setEventForm(updatedEventForm);
  };

  const backClickHandler = () => {
    setSelectedRows(null);
    clearSelectedEvent(null);
  };

  const formElementsArray = [];
  for (let key in eventForm) {
    formElementsArray.push({ id: key, config: eventForm[key] });
  }

  const form = (
    <form encType="multipart/form-data">
      {formElementsArray.map((formElement) => {
        if (
          formElement.id === "description" ||
          formElement.config?.elementType === "richtext"
        ) {
          return (
            <ReactQuill
              theme="snow"
              value={descriptionValue || ""}
              onChange={(content) => {
                setDescriptionValue(content);
                setSaveEnabled(true);
              }}
              key={formElement.id}
              className={classes.EventSubmission__Quill}
              modules={quillModules}
              formats={quillFormats}
            />
          );
        } else {
          return (
            <Input
              key={formElement.id}
              elementType={formElement.config.elementType}
              elementConfig={formElement.config.elementConfig}
              value={formElement.config.value ?? ""}
              changed={(e) => inputChangedHandler(e, formElement.id)}
              required={formElement.config.validation?.required}
              width={formElement.config.width}
            />
          );
        }
      })}
      <div className={classes.SlugPreview}>
        <small>
          <strong>Event Link:</strong> rbcommunity.org/event-
          {selectedEvent?.slug || "pending-save"}
        </small>
      </div>
    </form>
  );

  return (
    <div className={classes.EventSubmission}>
      <br />
      <br />

      <div className={classes.EventSubmission__TopInfo}>
        <h2>{selectedEvent ? `Edit an` : `Create a new`} event</h2>
        <div className={classes.EventSubmission__TopInfo__Buttons}>
          <Button disabled={!publishEnabled} clicked={handlePublish}>
            {publish ? `Unpublish` : `Publish`}
          </Button>
          <Button disabled={!saveEnabled} clicked={handleSubmit}>
            Save
          </Button>
        </div>
      </div>

      {form}
    </div>
  );
};

export default CreateEvent;
